import { get, post } from '../api';
import { showToast } from '../components/toast';
import { state } from '../state';
import { applyCreatedTextChannel } from '../layout/channel-list';
import type { ServerChannel } from '../types';

interface Message {
  id: string;
  content: string;
  author_id: string;
  created_at: string;
}

interface BoardCard {
  channelId: string;
  channelName: string;
  lastMessage: string;
  lastAuthor: string;
  lastTime: string;
  messageCount: number;
  pinned: boolean;
  position: number;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

export function cleanupBoard(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function fmtRelative(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  return d.toLocaleDateString('en-AU', {
    timeZone: 'Australia/Sydney',
    day: 'numeric', month: 'short',
  });
}

function getPinnedSet(serverId: string, category: string): Set<string> {
  try {
    const key = `hub-people-board-pins:${serverId}:${category}`;
    const raw = localStorage.getItem(key);
    return new Set(raw ? JSON.parse(raw) as string[] : []);
  } catch { return new Set(); }
}

function savePinnedSet(serverId: string, category: string, pins: Set<string>): void {
  const key = `hub-people-board-pins:${serverId}:${category}`;
  localStorage.setItem(key, JSON.stringify([...pins]));
}

function getPositions(serverId: string, category: string): Record<string, number> {
  try {
    const key = `hub-people-board-pos:${serverId}:${category}`;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as Record<string, number> : {};
  } catch { return {}; }
}

function savePositions(serverId: string, category: string, pos: Record<string, number>): void {
  const key = `hub-people-board-pos:${serverId}:${category}`;
  localStorage.setItem(key, JSON.stringify(pos));
}

async function loadCards(serverId: string, category: string): Promise<BoardCard[]> {
  // Match channels in this exact category OR any sub-category (Parent/Sub).
  // e.g. category="Notes" matches "Notes" and "Notes/My Notes".
  const channels = state.channels.filter(
    c => c.server_id === serverId && (
      c.category === category ||
      (c.category && c.category.startsWith(category + '/'))
    )
  );
  const pins = getPinnedSet(serverId, category);
  const positions = getPositions(serverId, category);

  const cards: BoardCard[] = [];
  for (const ch of channels) {
    let lastMsg = '';
    let lastAuthor = '';
    let lastTime = '';
    let count = 0;
    try {
      const msgs = await get<Message[]>(
        `/api/messages?server_id=${encodeURIComponent(serverId)}&channel=${encodeURIComponent(ch.name)}&limit=1`
      );
      if (msgs.length > 0) {
        const m = msgs[0] as Message;
        lastMsg = m.content.length > 120 ? m.content.slice(0, 120) + '...' : m.content;
        lastAuthor = m.author_id;
        lastTime = m.created_at;
      }
      // Get count from a lightweight endpoint if available
      count = msgs.length > 0 ? 1 : 0; // Placeholder — actual count would need a separate query
    } catch { /* swallow */ }
    cards.push({
      channelId: ch.id,
      channelName: ch.name,
      lastMessage: lastMsg,
      lastAuthor: lastAuthor,
      lastTime: lastTime,
      messageCount: count,
      pinned: pins.has(ch.id),
      position: positions[ch.id] ?? ch.position ?? 0,
    });
  }

  // Sort: pinned first (by position), then unpinned by position
  cards.sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return a.position - b.position;
  });

  return cards;
}

function renderCard(card: BoardCard): string {
  const authorName = (() => {
    const p = state.people.find(p => p.id === card.lastAuthor);
    return p?.name || '';
  })();
  const preview = card.lastMessage
    ? `<div class="board-card-preview">${esc(card.lastMessage)}</div>`
    : `<div class="board-card-preview board-card-empty">No messages yet</div>`;
  const timeStr = card.lastTime ? fmtRelative(card.lastTime) : '';
  const pinIcon = card.pinned
    ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>'
    : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>';

  return `
    <div class="board-card ${card.pinned ? 'board-card-pinned' : ''}"
         data-card-id="${esc(card.channelId)}"
         data-channel="${esc(card.channelName)}"
         draggable="${card.pinned ? 'false' : 'true'}">
      <div class="board-card-header">
        <span class="board-card-title"># ${esc(card.channelName)}</span>
        <div class="board-card-actions">
          <button class="board-card-btn board-pin-btn" data-card-id="${esc(card.channelId)}" title="${card.pinned ? 'Unpin' : 'Pin'}">${pinIcon}</button>
          <button class="board-card-btn board-open-btn" data-card-id="${esc(card.channelId)}" data-channel="${esc(card.channelName)}" title="Open channel">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </button>
        </div>
      </div>
      ${preview}
      <div class="board-card-footer">
        ${authorName ? `<span class="board-card-author">${esc(authorName)}</span>` : ''}
        ${timeStr ? `<span class="board-card-time">${esc(timeStr)}</span>` : ''}
      </div>
      <div class="board-card-quick">
        <input class="board-quick-input" data-channel="${esc(card.channelName)}" placeholder="Quick add..." maxlength="2000" />
        <button class="board-quick-send" data-channel="${esc(card.channelName)}" title="Send">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
        </button>
      </div>
    </div>
  `;
}

function wireCards(container: HTMLElement, serverId: string, category: string, switchChannel: (ch: string) => void): void {
  const pins = getPinnedSet(serverId, category);
  const positions = getPositions(serverId, category);

  // Pin toggle
  container.querySelectorAll<HTMLButtonElement>('.board-pin-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = btn.dataset['cardId'] || '';
      if (pins.has(id)) pins.delete(id);
      else pins.add(id);
      savePinnedSet(serverId, category, pins);
      void paint(container, serverId, category, switchChannel);
    });
  });

  // Open channel
  container.querySelectorAll<HTMLButtonElement>('.board-open-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const ch = btn.dataset['channel'] || '';
      if (ch) switchChannel(ch);
    });
  });

  // Card click = open channel
  container.querySelectorAll<HTMLElement>('.board-card').forEach(card => {
    card.addEventListener('click', (e) => {
      // Don't open if clicking on input, button, or quick-add area
      const target = e.target as HTMLElement;
      if (target.closest('.board-card-quick') || target.closest('.board-card-btn')) return;
      const ch = card.dataset['channel'] || '';
      if (ch) switchChannel(ch);
    });
  });

  // Quick add
  container.querySelectorAll<HTMLInputElement>('.board-quick-input').forEach(input => {
    const sendBtn = input.parentElement?.querySelector('.board-quick-send') as HTMLButtonElement | null;
    const doSend = async (): Promise<void> => {
      const content = input.value.trim();
      if (!content) return;
      const channel = input.dataset['channel'] || '';
      try {
        await post('/api/messages', {
          server_id: serverId,
          channel,
          content,
          author_id: state.ownerId || '',
        });
        input.value = '';
        showToast('Sent', 'ok');
        // Refresh the card preview
        void paint(container, serverId, category, switchChannel);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to send', 'err');
      }
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void doSend(); }
    });
    input.addEventListener('click', (e) => e.stopPropagation());
    sendBtn?.addEventListener('click', (e) => { e.stopPropagation(); void doSend(); });
  });

  // Drag reorder (non-pinned cards only)
  container.querySelectorAll<HTMLElement>('.board-card[draggable="true"]').forEach(card => {
    card.addEventListener('dragstart', (e) => {
      card.classList.add('board-card-dragging');
      e.dataTransfer?.setData('text/plain', card.dataset['cardId'] || '');
    });
    card.addEventListener('dragend', () => {
      card.classList.remove('board-card-dragging');
    });
  });

  const grid = container.querySelector('.board-grid');
  if (grid) {
    grid.addEventListener('dragover', (e) => {
      e.preventDefault();
      const dragging = grid.querySelector('.board-card-dragging');
      if (!dragging) return;
      const afterEl = getDragAfterElement(grid as HTMLElement, (e as DragEvent).clientY);
      if (afterEl) grid.insertBefore(dragging, afterEl);
      else grid.appendChild(dragging);
    });

    grid.addEventListener('drop', (e) => {
      e.preventDefault();
      // Save new positions based on DOM order
      const cards = grid.querySelectorAll<HTMLElement>('.board-card');
      cards.forEach((card, i) => {
        const id = card.dataset['cardId'] || '';
        if (id) positions[id] = i;
      });
      savePositions(serverId, category, positions);
    });
  }
}

function getDragAfterElement(container: HTMLElement, y: number): HTMLElement | null {
  const cards = [...container.querySelectorAll<HTMLElement>('.board-card:not(.board-card-dragging)')];
  let closest: { el: HTMLElement | null; offset: number } = { el: null, offset: Number.NEGATIVE_INFINITY };
  for (const card of cards) {
    const box = card.getBoundingClientRect();
    const offset = y - box.top - box.height / 2;
    if (offset < 0 && offset > closest.offset) {
      closest = { el: card, offset };
    }
  }
  return closest.el;
}

async function paint(container: HTMLElement, serverId: string, category: string, switchChannel: (ch: string) => void): Promise<void> {
  const grid = container.querySelector('.board-grid');
  if (!grid) return;
  try {
    const cards = await loadCards(serverId, category);
    if (cards.length === 0) {
      grid.innerHTML = `<div class="board-empty">No channels in this category yet. Right-click to create one.</div>`;
      return;
    }
    grid.innerHTML = cards.map(renderCard).join('');
    wireCards(container, serverId, category, switchChannel);
  } catch {
    grid.innerHTML = `<div class="board-empty">Failed to load board.</div>`;
  }
}

export async function renderBoard(body: HTMLElement, category: string): Promise<void> {
  cleanupBoard();
  const serverId = state.currentServer || '';
  // Resolve the actual category name from the channel_config if available,
  // or use the category passed in.
  const displayCat = category;

  // Switch to a channel by setting state + re-rendering the app.
  const switchChannel = (ch: string): void => {
    state.currentChannel = ch;
    void import('../app').then(({ renderApp }) => renderApp());
  };

  body.innerHTML = `
    <div class="board-pane">
      <div class="board-toolbar">
        <h2 class="board-heading">${esc(displayCat)}</h2>
        <div class="board-toolbar-right">
          <button class="btn btn-secondary btn-sm" id="board-add-channel" type="button">+ Channel</button>
          <button class="btn btn-secondary btn-sm" id="board-refresh" type="button">Refresh</button>
        </div>
      </div>
      <div class="board-grid" id="board-grid">
        <div class="board-empty">Loading...</div>
      </div>
    </div>
  `;

  await paint(body, serverId, displayCat, switchChannel);

  document.getElementById('board-refresh')?.addEventListener('click', () => {
    void paint(body, serverId, displayCat, switchChannel);
  });

  document.getElementById('board-add-channel')?.addEventListener('click', () => {
    void import('../components/modal').then(({ showModal, closeModal, modalFrame }) => {
      showModal(modalFrame('Create Channel',
        `<div class="form-group">
          <label class="form-label" for="bc-name">Channel Name</label>
          <input class="form-input" id="bc-name" placeholder="my-notes" maxlength="100" autocomplete="off">
        </div>`,
        `<button class="btn btn-secondary" id="bc-cancel">Cancel</button>
         <button class="btn btn-primary" id="bc-create">Create</button>`
      ));
      document.getElementById('modal-close')?.addEventListener('click', closeModal);
      document.getElementById('bc-cancel')?.addEventListener('click', closeModal);
      const input = document.getElementById('bc-name') as HTMLInputElement | null;
      input?.focus();
      document.getElementById('bc-create')?.addEventListener('click', () => {
        const name = (input?.value || '').trim().toLowerCase().replace(/\s+/g, '-');
        if (!name) { input?.focus(); return; }
        void post<ServerChannel>('/api/channels/create', {
          server_id: serverId,
          name,
          channel_type: 'text',
          category: displayCat,
        }).then((ch) => {
          applyCreatedTextChannel(ch);
          closeModal();
          showToast(`#${name} created`, 'ok');
          // Refresh board cards AND the sidebar channel list so the new
          // channel appears in both without a page reload.
          void paint(body, serverId, displayCat, switchChannel);
          void import('../app').then(({ renderApp }) => renderApp());
        }).catch(e => showToast(String(e), 'err'));
      });
    });
  });

  // Poll every 30s for card previews
  pollTimer = setInterval(() => {
    if (!document.querySelector('.board-pane')) {
      if (pollTimer) clearInterval(pollTimer);
      return;
    }
    void paint(body, serverId, displayCat, switchChannel);
  }, 30_000);
}
